import { Link } from "react-router-dom";
import {
  HeartHandshake,
  Home as HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
  User,
  Wind,
  Users,
  GraduationCap,
  PhoneCall,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Phone,
} from "lucide-react";
import { SITE, SERVICES, VALUES, OUTCOMES } from "@/lib/site";
import { HOME_PAGE } from "@/constants/testIds";
import { CqcBadge } from "@/components/CqcBadge";
import { TopBanner } from "@/components/TopBanner";
import { HeroVideoCard, HeroVideoButton } from "@/components/VideoModal";

const ICONS = {
  HeartHandshake,
  Home: HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
  User,
  Wind,
  Users,
  GraduationCap,
  PhoneCall,
  ShieldCheck,
};

const Icon = ({ name, className }) => {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} strokeWidth={1.5} />;
};

export default function Home() {
  return (
    <div>
      <TopBanner />
      {/* HERO — FULL-WIDTH BANNER */}
      <section
        data-testid={HOME_PAGE.hero}
        className="relative w-full overflow-hidden bg-brand-ink"
      >
        <div className="relative w-full h-[68vh] min-h-[520px] max-h-[760px]">
          <img
            src={SITE.imagery.heroBanner}
            alt="Embrace Healthcare carer in purple uniform sharing a warm moment with an elderly lady at home"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          {/* Readability gradient: stronger on the left for text */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/90 via-brand-ink/70 to-brand-ink/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent" />

          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div className="max-w-2xl text-white fade-up">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand-blush whitespace-nowrap">
                <span className="w-8 h-px bg-brand-blush" /> Care in Doncaster &amp; Leicester
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.02] mt-5 !text-white drop-shadow-lg">
                Compassionate care
                <br />
                <em className="not-italic !text-brand-blush">in the comfort</em>
                <br />
                of your own home.
              </h1>
              <p className="mt-7 text-lg lg:text-xl text-white/85 max-w-xl leading-relaxed">
                At Embrace Healthcare we deliver high-quality, person-centred
                domiciliary care across Doncaster and Leicester — built around
                your routines, your preferences and the life you want to lead.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/services"
                  data-testid={HOME_PAGE.heroCtaPrimary}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 shadow-lg"
                >
                  Explore our services
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid={HOME_PAGE.heroCtaSecondary}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-sm text-white px-7 py-4 text-base font-semibold hover:bg-white hover:text-brand-ink transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Phone className="w-4 h-4" /> Call {SITE.phone}
                </a>
                <HeroVideoButton />
              </div>
            </div>
          </div>

          <HeroVideoCard />
        </div>

        {/* CQC strip directly under banner */}
        <div className="bg-white border-b border-brand-line">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CqcBadge />
            <p className="text-sm text-brand-mute">
              Proudly serving <span className="font-semibold text-brand-ink">Doncaster</span> and <span className="font-semibold text-brand-ink">Leicester</span> — at home, with dignity.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section
        data-testid={HOME_PAGE.trustBar}
        className="border-y border-brand-line bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {[
            { label: "CQC Regulated", icon: ShieldCheck },
            { label: "Person-Centred Care", icon: User },
            { label: "Consistent Care Teams", icon: Users },
            { label: "24/7 Care Available", icon: PhoneCall },
          ].map((b) => (
            <div
              key={b.label}
              className="flex items-center gap-3 text-brand-ink"
            >
              <b.icon className="w-5 h-5 text-brand" strokeWidth={1.7} />
              <span className="text-sm font-semibold tracking-wide">
                {b.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        data-testid={HOME_PAGE.whyChooseUs}
        className="py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Why Choose Embrace
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
              Great care starts with{" "}
              <em className="not-italic text-brand">listening</em>.
            </h2>
            <p className="mt-5 text-lg text-brand-mute leading-relaxed">
              We don't believe in a one-size-fits-all approach. Our dedicated
              team is passionate about making a positive difference every day —
              and providing care that truly embraces the individual.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <article
                key={v.title}
                className="group relative bg-white border border-brand-line rounded-3xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-soft flex items-center justify-center mb-6 group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                  <Icon
                    name={v.icon}
                    className="w-6 h-6 text-brand group-hover:text-white transition-colors duration-300"
                  />
                </div>
                <h3 className="font-display text-2xl mb-3 text-brand-ink">
                  {v.title}
                </h3>
                <p className="text-brand-mute leading-relaxed text-[15px]">
                  {v.body}
                </p>
                <span className="absolute top-8 right-8 text-xs font-mono text-brand-mute/50">
                  0{i + 1}
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section
        data-testid={HOME_PAGE.servicesGrid}
        className="py-20 lg:py-28 bg-brand-cream"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
                <span className="w-8 h-px bg-brand" /> What we offer
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
                Flexible support packages, built entirely around you.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-brand-mute leading-relaxed">
                From occasional domestic help to comprehensive daily assistance
                and specialist complex care — we design care that promotes
                independence, dignity, choice and wellbeing.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                data-testid={HOME_PAGE.serviceCard(s.slug)}
                className="group bg-white border border-brand-line rounded-3xl p-7 hover:bg-brand hover:text-white transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center mb-5 group-hover:bg-white/15 transition-colors">
                  <Icon
                    name={s.icon}
                    className="w-5 h-5 text-brand group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="font-display text-xl mb-3 group-hover:text-white transition-colors">
                  {s.title}
                </h3>
                <p className="text-sm text-brand-mute group-hover:text-white/85 leading-relaxed transition-colors">
                  {s.summary}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-3 bg-brand-soft rounded-[2rem] rotate-2" />
              <img
                src={SITE.imagery.servicesOverview}
                alt="Carer helping with domestic tasks at home"
                className="relative w-full h-[460px] object-cover rounded-[1.75rem] shadow-xl"
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Supporting better outcomes
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
              Care that helps people live{" "}
              <em className="not-italic text-brand">life on their terms.</em>
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {OUTCOMES.map((o) => (
                <li key={o} className="flex items-start gap-3 text-brand-ink">
                  <CheckCircle2
                    className="w-5 h-5 text-brand mt-1 shrink-0"
                    strokeWidth={1.8}
                  />
                  <span className="text-[15px] lg:text-base leading-relaxed">
                    {o}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid={HOME_PAGE.finalCta}
        className="py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-brand-cream border border-brand-line rounded-[2rem] p-10 lg:p-16 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-soft blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Talk to us about a{" "}
                  <em className="not-italic text-brand">care assessment.</em>
                </h2>
                <p className="mt-5 text-lg text-brand-mute max-w-2xl">
                  Whether for yourself or someone you love, our team is ready
                  to listen. Speak to us today and we'll design a care package
                  built around the things that matter most.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 shadow-sm w-full lg:w-auto"
                >
                  <Phone className="w-4 h-4" /> Call {SITE.phone}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 bg-white text-brand-ink px-7 py-4 text-base font-semibold hover:bg-brand-soft transition w-full lg:w-auto"
                >
                  All contact details <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
